import React, { useCallback, useState, useEffect } from 'react';
import { Coordinate, VideoOverlayObject, Video, PivotOption } from '../VideoPlayer/VideoTypes';
import { getFlagIcon, Flag, FlagIssue, FlagStatus } from '../Sessions/Label/useValidationFlags';
import { PositionBuffer, FrameRange } from '../Sessions/Label/SessionLabelTask';

const flagIconSize: number = 32;
const tooltipWidth: number = 150;
const tooltipHeight: number = 130;

const metadataString = (flag: Flag) => {
  switch(flag.issue) {
    case FlagIssue.InhumanSpeed:
    case FlagIssue.LongInterpolation:
      return "Speed: " + ((flag.metadata.average_speed ?? flag.metadata.speed ?? 0)*3.6).toFixed(1) + " km/h";
    case FlagIssue.PositionOverlap:
      return "Other task: " + flag.metadata.other_task;
    default:
      return "Unknown issue";
  }
};

type HoverFlag = {
  flag: Flag;
  coord: Coordinate;
};

type Props = {
  flags: Flag[];
  buffers: PositionBuffer[];
  excludeRanges: FrameRange[];
  video: Video;
  setFlagStatus?: (flag: Flag) => void;
  seekToFlag?: (flag: Flag) => void;
  disableSelection?: boolean;
};

const FlagMarkers = (props: Props) => {
  const {
    flags,
    buffers,
    excludeRanges,
    video,
    setFlagStatus,
    seekToFlag,
    disableSelection,
  } = props;
  
  const [selectedFlag, setSelectedFlag] = useState<HoverFlag | undefined>(undefined);

  const unselect = useCallback(() => {
    setSelectedFlag(undefined);
  }, []);

  const flagSelect = useCallback((flag: HoverFlag) => {
    if(disableSelection) {
      unselect();
      return;
    }
    setSelectedFlag(prev => {
      if(prev && flag.flag.id === prev.flag.id) {
        return undefined;
      } else {
        return flag;
      }
    });
  }, [disableSelection, unselect]);

  const toggleStatusSelected = useCallback(() => {
    if(!selectedFlag) return;
    setFlagStatus && setFlagStatus(selectedFlag.flag);
    setSelectedFlag(undefined);
  }, [selectedFlag, setFlagStatus]);

  const seekToFlagPressed = useCallback(() => {
    if(!selectedFlag) return;
    seekToFlag && seekToFlag(selectedFlag.flag);
  }, [seekToFlag, selectedFlag]);

  // If flag is removed (solved) - we unselect it if needed
  useEffect(() => {
    if(flags.find(f => f.id === selectedFlag?.flag.id) === undefined) {
      unselect();
    }
  }, [flags, selectedFlag, unselect]);

  const overlayObjects: VideoOverlayObject[] = [];
  flags
    .filter((flag: Flag) => {
      const notInExclusionRange: boolean = excludeRanges.find((range: FrameRange) => 
        (range.start_frame >= flag.start && range.start_frame <= flag.end)
        || (range.end_frame && (range.end_frame >= flag.start && range.end_frame <= flag.end))
      ) === undefined;
      return notInExclusionRange;
    })
    .forEach((flag: Flag) => {
      const startBuffer: PositionBuffer | undefined = buffers.find(x => x.page_start <= flag.start && (x.page_start+x.page_size) > flag.start);
      const startCoords: Coordinate[] | undefined = startBuffer?.video_coordinates?.get(String(video.id));
      if(!startBuffer || !startCoords) {
        if(flag.id === selectedFlag?.flag.id) {
          setSelectedFlag(undefined);
        }
        return;
      }
      const startPos: Coordinate = startCoords[flag.start-startBuffer.page_start];
      const flagColor: string = flag.status === FlagStatus.Unresolved ? "#900a" : "#090a";

      const iconGfx = (
        <svg viewBox="0 0 100 200">
          {/* Background */}
          <rect
            width={100}
            height={100}
            fill={flagColor}
          />
          <rect 
            x={10}
            y={10}
            width={80}
            height={80}
            fill={"#fffc"}
          />
          {/* Icon Graphic */}
          <svg
            x={15}
            y={15}
            width={70}
            height={70}
          >
            {getFlagIcon(flag)}
          </svg>
          
          {/* Hitbox */}
          <rect
            width="100%"
            height="100%"
            opacity={0}
            onClick={() => flagSelect(selectedObj)}
          />

          {/* Line to marker */}
          <line
            x1={50}
            x2={50}
            y1={100}
            y2={200}
            strokeWidth={16}
            stroke={flagColor}
          />
        </svg>
      );

    const selectedObj: HoverFlag = {
      flag: flag,
      coord: startPos,
    }
    
    const overlayObject: VideoOverlayObject = {
      center: startPos,
      svg: iconGfx,
      width: flagIconSize,
      height: flagIconSize*2,
      pivot: PivotOption.BottomCenter,
    };
    overlayObjects.push(overlayObject);
  });

  /* Selected flag tooltip overlay object */
  if(selectedFlag) {
    const tooltipSvg = (
      <svg>
        {/* Background tooltip */}
        <rect
          width="100%"
          height="100%"
          fill="#cccc"
        />
        {/* Text */}
        <svg viewBox={"0 0 "+tooltipWidth+" "+tooltipHeight+""}>
          {/* Title */}
          <text
            x={tooltipWidth*0.5} 
            y={30}
            fontSize={14}
            fontWeight="bold"
            dominantBaseline="middle" 
            textAnchor="middle"
          >
            { selectedFlag.flag.issue }
          </text>
          {/* Metadata */}
          <text
            fontSize={10}
          >
            <tspan x={25} y={54}>ID: {selectedFlag.flag.id}</tspan>
            <tspan x={25} y={70}>Frame: {selectedFlag.flag.start} - {selectedFlag.flag.end}</tspan>
            <tspan x={25} y={86}>{metadataString(selectedFlag.flag)}</tspan>
          </text>

          {/* Set Allow Button */}
          <rect
            x={tooltipWidth*0.5-50}
            y={tooltipHeight-30}
            width={60}
            height={20}
            fill={selectedFlag.flag.status === FlagStatus.Unresolved ? "#090" : "#900"}
          />
          {/* Text */}
          <text
            x={tooltipWidth*0.5-20} 
            y={tooltipHeight-20}
            fontSize={14}
            dominantBaseline="middle" 
            textAnchor="middle"
            fill="#fff"
          >
            {selectedFlag.flag.status === FlagStatus.Unresolved ? "Allow" : "Disallow"}
          </text>
          {/* Hit box */}
          <rect
            x={tooltipWidth*0.5-50}
            y={tooltipHeight-30}
            width={60}
            height={20}
            opacity={0}
            onClick={toggleStatusSelected}
          />

          {/* Go-to Button */}
          <rect
            x={tooltipWidth*0.5+20}
            y={tooltipHeight-30}
            width={30}
            height={20}
            fill="#009"
          />
          {/* Text */}
          <text
            x={tooltipWidth*0.5+35} 
            y={tooltipHeight-20}
            fontSize={14}
            dominantBaseline="middle" 
            textAnchor="middle"
            fill="#fff"
          >
            &gt;
          </text>
          {/* Hit box */}
          <rect
            x={tooltipWidth*0.5+20}
            y={tooltipHeight-30}
            width={30}
            height={20}
            opacity={0}
            onClick={seekToFlagPressed}
          />

          {/* Close Button */}
          <rect
            x={tooltipWidth-20}
            y={0}
            width={20}
            height={20}
            fill="#900"
          />
          {/* Text */}
          <text
            x={tooltipWidth-10} 
            y={11}
            fontSize={14}
            dominantBaseline="middle" 
            textAnchor="middle"
            fill="#fff"
          >
            X
          </text>
          {/* Hit box */}
          <rect
            x={tooltipWidth-20}
            y={0}
            width={20}
            height={20}
            opacity={0}
            onClick={unselect}
          />
        </svg>
      </svg>
    );

    const selectOverlayObject: VideoOverlayObject = {
      center: selectedFlag.coord,
      svg: tooltipSvg,
      width: tooltipWidth,
      height: tooltipHeight,
      pivot: PivotOption.BottomLeft,
    }
    overlayObjects.push(selectOverlayObject);
  }

  return overlayObjects;
}

export default FlagMarkers;